import { Box } from 'src/design/atoms/Box';
import { Divider } from 'src/design/atoms/Divider';
import { Group } from 'src/design/atoms/Group';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { Checkbox } from 'src/design/atoms/Checkbox';
import { Tabs } from 'src/design/molecules/Tabs';
import React from 'react';
import { logger } from 'src/utils';
import { parseWorkWeekId } from 'src/utils/workWeek';
import { useTimesheetSummary } from 'src/api/queries/timesheets/useTimesheetSummary';
import { useWorkflowByOffice } from 'src/api/queries/workflows/useWorkflowByOffice';
import { OfficeSelector } from 'src/app/selectors/OfficeSelectorSingle';
import { DescriptionText } from 'src/design/atoms/DescriptionText';
import { useAuth } from 'src/hooks/useAuth';
import { useOrganization } from 'src/hooks/useOrganization';
import { PageBreadcrumbs } from 'src/design/molecules/PageBreadcrumbs';
import { WeekSelector } from 'src/routes/Jobs/Timesheets/components/WeekSelector';
import { MissingTimesheetsCard } from 'src/routes/Time/MissingTimesheetsCard';
import { TimesheetTab } from 'src/routes/Time/TimesheetTab/index';
import { TimesheetSummary } from 'src/api/models/timesheet';
import { useTimeViewParams } from 'src/routes/Time/hooks/useTimeViewParams';
import {
  resolveTimeTab,
  TIME_TAB_DRAFT,
  TIME_TAB_MISSING,
  TIME_TAB_REJECTED,
} from 'src/routes/Time/timeSearchParams';

export const Time: React.FC = () => {
  const { loading: loadingOffices, offices } = useOrganization();
  const { user } = useAuth();
  const {
    tab,
    officeId: urlOfficeId,
    selectedWeek,
    onlyMyTeam: showOnlyMyTeam,
    setTab,
    setOfficeId,
    setWeek,
    setOnlyMyTeam,
  } = useTimeViewParams();

  const officeId = React.useMemo(
    () =>
      offices.some((office) => office.id === urlOfficeId) ? urlOfficeId : null,
    [offices, urlOfficeId],
  );
  const { year: selectedYear, weekNumber: selectedWeekId } = parseWorkWeekId(
    selectedWeek.id,
  );

  const { data: summaryData } = useTimesheetSummary(officeId, {
    year: selectedYear,
    weekId: selectedWeekId,
    onlyMyTeam: showOnlyMyTeam || undefined,
  });
  const summary: TimesheetSummary = summaryData ?? {};

  const { data: workflowData, isLoading: loadingWorkflow } =
    useWorkflowByOffice(officeId, 'timesheet');
  const workflow = workflowData ?? null;

  const activeTab = resolveTimeTab(tab, workflow?.steps.length ?? 0);

  const tabContainer = React.useMemo(() => {
    if (!workflow) {
      return <></>;
    }

    const tabs = workflow.steps.map((step, index) => {
      const tabIndex = index + 1;
      const tabLabel = step.displayLabel ?? step.label;
      const summaryValue = summary[step.label] || 0;
      return (
        <Tabs.Tab
          key={step.id}
          value={String(tabIndex)}
        >{`${tabLabel} (${summaryValue})`}</Tabs.Tab>
      );
    });

    const tabPanels = workflow.steps.map((step, index) => {
      const tabIndex = index + 1;
      return (
        <Tabs.Panel key={step.id} value={String(tabIndex)}>
          <Stack gap="xs">
            {step.description && (
              <Text size="sm" c="dimmed">
                {step.description}
              </Text>
            )}
            <TimesheetTab
              key={step.id}
              status={step.label}
              workflow={workflow}
              stepIndex={index}
              officeId={officeId}
              weekId={selectedWeekId}
              year={selectedYear}
              supervisorId={showOnlyMyTeam ? user?.id : undefined}
            />
          </Stack>
        </Tabs.Panel>
      );
    });

    const draftSummaryValue = summary.draft || summary.Draft || 0;
    const rejectedSummaryValue = summary.rejected || summary.Rejected || 0;
    return (
      <Tabs keepMounted={false} value={activeTab} onChange={setTab}>
        <Tabs.List>
          <Tabs.Tab
            value={TIME_TAB_DRAFT}
          >{`Draft (${draftSummaryValue})`}</Tabs.Tab>
          {...tabs}
          <Tabs.Tab value={TIME_TAB_MISSING}>Missing Time</Tabs.Tab>
          <Tabs.Tab
            value={TIME_TAB_REJECTED}
          >{`Rejected (${rejectedSummaryValue})`}</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value={TIME_TAB_REJECTED}>
          <TimesheetTab
            workflow={workflow}
            status="Rejected"
            officeId={officeId}
            weekId={selectedWeekId}
            year={selectedYear}
            supervisorId={showOnlyMyTeam ? user?.id : undefined}
          />
        </Tabs.Panel>
        <Tabs.Panel value={TIME_TAB_DRAFT}>
          <TimesheetTab
            workflow={workflow}
            status="Draft"
            officeId={officeId}
            weekId={selectedWeekId}
            year={selectedYear}
            supervisorId={showOnlyMyTeam ? user?.id : undefined}
          />
        </Tabs.Panel>
        {...tabPanels}
        <Tabs.Panel value={TIME_TAB_MISSING}>
          <MissingTimesheetsCard
            officeId={officeId}
            weekId={selectedWeekId}
            year={selectedYear}
          />
        </Tabs.Panel>
      </Tabs>
    );
  }, [
    workflow,
    activeTab,
    setTab,
    summary,
    officeId,
    selectedWeekId,
    selectedYear,
    showOnlyMyTeam,
    user?.id,
  ]);

  return (
    <Stack>
      <PageBreadcrumbs items={[{ text: 'Time' }]} />
      <Stack gap="xs">
        <Text fw={700}>Office</Text>
        {!loadingOffices ? (
          <Group>
            <Box w={300}>
              <OfficeSelector
                initialOfficeId={officeId}
                initialValue={''}
                onChange={() => logger.debug('Office selector change')}
                onOfficeChange={(office) => setOfficeId(office?.id ?? null)}
              />
            </Box>
            <Box w={300}>
              <WeekSelector hideLabel value={selectedWeek} onChange={setWeek} />
            </Box>
            <Divider orientation="vertical" />
            <Checkbox
              label="Show Only My Team"
              description="Only show timesheets for users that are supervised by you"
              checked={showOnlyMyTeam}
              onChange={(e) => setOnlyMyTeam(e.currentTarget.checked)}
            />
          </Group>
        ) : (
          <div>Loading offices...</div>
        )}
        <DescriptionText>
          Select an office to manage time entries for your team.
        </DescriptionText>
      </Stack>
      {!loadingWorkflow ? (
        tabContainer
      ) : (
        <>{officeId && <div>Loading workflow...</div>}</>
      )}
    </Stack>
  );
};
